
/**
 * Network diagnostic utilities for troubleshooting connection issues
 */

export interface NetworkDiagnosticResult {
  internetConnected: boolean;
  supabseConnected: boolean;
  latency: number | null;
  timestamp: string;
  errorDetails?: string;
  browserInfo?: {
    userAgent: string;
    platform: string;
    language: string;
    online: boolean;
  };
  connectionInfo?: {
    type?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
  };
}

/**
 * Run diagnostics to troubleshoot connection issues
 */
export const diagnoseConnectionIssues = async (): Promise<NetworkDiagnosticResult> => {
  const startTime = Date.now();
  let result: NetworkDiagnosticResult = {
    internetConnected: navigator.onLine,
    supabseConnected: false,
    latency: null,
    timestamp: new Date().toISOString(),
    browserInfo: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      online: navigator.onLine
    }
  };

  try {
    // Get connection info if available
    if (navigator.connection) {
      const conn = (navigator as any).connection;
      result.connectionInfo = {
        type: conn.type,
        effectiveType: conn.effectiveType,
        downlink: conn.downlink,
        rtt: conn.rtt
      };
    }
    
    // Test basic connectivity with a small fetch request
    const connectivityTest = await Promise.race([
      fetch('https://www.google.com/generate_204', { method: 'HEAD', cache: 'no-store' }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000))
    ]);
    
    result.internetConnected = (connectivityTest as Response).ok;
    
    // Test Supabase connection using a fetch to their status page
    // This just tests if we can reach Supabase infrastructure, not specifically our project
    const supabaseTest = await Promise.race([
      fetch('https://status.supabase.com/api/v1/summary.json', { cache: 'no-store' }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 5000))
    ]);
    
    result.supabseConnected = (supabaseTest as Response).ok;
    result.latency = Date.now() - startTime;
    
  } catch (error) {
    console.error('Error during network diagnostics:', error);
    result.errorDetails = error instanceof Error ? error.message : String(error);
  }
  
  return result;
};
